/* ============================================================================
   hh-pdp.js — H&H product page interaction (v9 build; RT-742 accessibility layer). Progressive enhancement.
   RT-742 (P35 RT-671 items F-01, F-02, F-04, F-13, F-17 (c), F-18): see the RT-742 blocks below. Version stamp:
   the string HH-PDP-RT742 in this header is what the after-proof reads to know the served script is this one.

   TWO concerns, one DOMContentLoaded:
   1) BUY COLUMN selectors: the custom option buttons map the chosen combination to its variant
      and update the hidden /cart/add `id`, the price, the Key-specs weight, and the stock line.
      The page is purchasable with JS OFF (the hidden id defaults to the first available variant).
      No required-gating (options default to the first).
      RT-216 rebuilt the commit path for SPARSE variant grids, where the shopper can name a
      combination the product does not sell. It resolves a variant first and derives the whole
      selection from it, repairing the untouched axes rather than leaving the page describing one
      item and carrying another, and marks the values that cannot combine with the current
      selection. See the block comment above `fitWeight` for the measurements and the reasoning.
   2) GALLERY (v9, new): the Colour swatch drives a PER-COLOUR rail. Selecting a colour sets the
      hero to that colour's FRONT view (imgs[0]) and builds the rail from the rest; a colour with a
      single image hides the rail; clicking a rail thumbnail PROMOTES it to the hero; clicking the
      hero opens a full-resolution lightbox (Esc / click to close). Data comes from the
      [data-hh-gallery] island (hh-gallery.liquid), keyed off hnh.color_images with a native
      fallback. With JS OFF the hero shows the server-rendered front image (no rail) — graceful.
   ============================================================================ */
(function () {
  'use strict';

  /* ---------------------------- gallery module ---------------------------- */
  function initGallery() {
    var dataEl = document.querySelector('[data-hh-gallery]');
    var hero = document.querySelector('[data-hh-hero]');
    var heroImg = document.querySelector('[data-hh-heroimg]');
    var rail = document.querySelector('[data-hh-rail]');
    if (!dataEl || !hero || !heroImg || !rail) return null;
    var G;
    try { G = JSON.parse(dataEl.textContent); } catch (e) { return null; }
    var lb = document.querySelector('[data-hh-lb]');
    var lbImg = document.querySelector('[data-hh-lbimg]');

    /* RT-611, Gallery Chip Specification v3 section 3.5, owner decision D103: "A chip should never
       be seen as the main photo." POSITION 1 IS A FILE OR IT IS THE "PHOTO COMING SOON" WELL.

       hh-gallery.liquid renders the well where position 1 has no file at load, and otherwise carries
       it in a <template data-hh-ph>, so its words stay in the one snippet that owns them. `ph` is
       that well, in the figure or detached. It is null on a page with no Color option and on a
       stale cached page, and every function below is then a no-op, so those pages execute what they
       executed before.

       ONE PAIR OF HELPERS FOR THE SWAP, so the zoom affordance follows position 1 on every path (a
       pick and a rail click alike): the zoom button (RT-742: the badge is the button now; the figure
       is role="presentation"), the title, the cursor and the lightbox all switch together. `heroSeq` drops a photograph that
       finishes loading after a later pick has already moved position 1 elsewhere. */
    var ph = hero.querySelector('.hh-ph');
    if (!ph) {
      var phTpl = hero.querySelector('template[data-hh-ph]');
      var phSrc = phTpl && phTpl.content ? phTpl.content.querySelector('.hh-ph') : null;
      if (phSrc) ph = phSrc.cloneNode(true);
    }
    var zoomBadge = hero.querySelector('.hh-hero__zoom');
    var heroSeq = 0;

    function wellShown() {
      return !!(ph && ph.parentNode === hero);
    }

    function showPhoto() {
      if (!ph) return;
      if (ph.parentNode) ph.parentNode.removeChild(ph);
      heroImg.style.display = '';
      /* RT-742 (P35 F-04, F-13): the figure is no longer a button. The picture keeps its own alt,
         read once, and the zoom control is the badge, a real <button> whose name is the words it
         prints. The figure keeps the hover tooltip only, and role="presentation" keeps that
         tooltip out of the accessibility tree. */
      if (zoomBadge) zoomBadge.style.display = '';
      hero.setAttribute('role', 'presentation');
      hero.setAttribute('title', 'Click to zoom');
      if (lb && lbImg) hero.style.cursor = 'zoom-in';
    }

    function showWell() {
      if (!ph) return false;
      heroSeq++;
      heroImg.style.display = 'none';
      if (zoomBadge) zoomBadge.style.display = 'none';
      hero.removeAttribute('role');
      hero.removeAttribute('title');
      hero.style.cursor = '';
      if (ph.parentNode !== hero) hero.appendChild(ph);
      return true;
    }

    function setHero(src, alt) {
      if (!src) return;
      var seq = ++heroSeq;
      var pre = new Image();
      pre.onload = function () {
        if (seq !== heroSeq) return;
        // box follows the image ratio but never taller than 2x wide (P77 hero-box rule)
        hero.style.aspectRatio = String(Math.max(pre.naturalWidth / pre.naturalHeight, 0.5));
        heroImg.src = src;
        if (alt != null) heroImg.alt = alt;
        showPhoto();
      };
      pre.src = src;
    }

    /* RT-391: THE RAIL IS THE COLOURWAY'S SET, THEN THE PAGE'S, AND AN ENTRY NAMES ITSELF.

       `page` holds the photographs that belong to the PAGE rather than to any one colourway - a
       range shot, a drawing sheet, the derailleur comparison in the owner's item 10 - which
       hh-gallery.liquid appends to every colourway as secondaries. They are a separate array
       because they cannot take the colourway's accessible name: announcing "Cage in Black" over a
       photograph of two derailleurs is the RT-222 defect wearing a different hat. Each carries its
       own alt, authored by P75/P34 to say what the photograph shows, and falling back in Liquid to
       the product line name, which names no colourway.

       So a rail entry is now `{src, alt}` rather than a bare URL. `railItems` is the one place the
       two arrays are joined and the only place the default alt is applied, so the hero, the rail
       labels and the lightbox still read one expression and nothing else in this file learns about
       page-level frames. `imgs` first keeps position 1, and therefore the hero, exactly where it
       was on every colourway in the store. */
    /* RT-400: THE SECOND AXIS. WHICH VARIANT A PHOTOGRAPH IS OF, NOT ONLY WHICH COLOUR.

       The owner's D8 of 2026-08-10: "These are variants and the image that shows on the website
       should be that of the variant selected", and on the pulleys, "the image shown should be the
       correct size (tooth count) for the size selected". Everything above this line resolves on
       the Colour option alone, so all six tooth-count sets sat on one colour's strip and selecting
       a set changed nothing.

       `axisOf` is hh-gallery.liquid's answer to "which value of a non-colour axis is this
       photograph of", one entry per photograph per axis it could answer for, and ABSENT for a
       photograph it could not. Absent means NEUTRAL: it shows at every value, which is the state
       of every photograph on 103 of the 108 published pages, so this whole module is inert there.

       `AX` stays null on a page with no such entry, and every function below short-circuits on
       that, so the no-axis path executes the same statements it executed before this leg.

       BOTH ISLAND FIELDS ARRIVE AS ARRAYS, not as objects, and the maps are built here. That is
       hh-gallery.liquid's constraint rather than a preference: this Liquid refuses to lex an
       output tag whose only content is a quoted closing brace, so a snippet cannot write a JSON
       object literal at all. `sel` is [name, value] pairs and `axisOf` is
       [filename, axis, value, came-from-a-bind]. */
    var AX = null;
    if (G.axisOf && G.axisOf.length) {
      AX = { sel: {}, byFile: {} };
      (G.sel || []).forEach(function (p) { AX.sel[p[0]] = p[1]; });
      G.axisOf.forEach(function (t) {
        var f = t[0];
        (AX.byFile[f] = AX.byFile[f] || []).push({ a: t[1], v: t[2], b: t[3] });
      });
    }

    /* RT-442: THE PHOTOGRAPH'S OWN ALT, WHICH IS THE ONE P75 AUTHORED TO SAY WHAT IT SHOWS.

       `alts` is hh-gallery.liquid's per-photograph lookup, as [filename, alt] pairs. Every rule
       and the one reservation are stated in that file; this side only applies them, so the two
       cannot drift into disagreeing about which string a page announces.

       A photograph with no authored alt has no row here, so `authoredAlt` returns '' and every
       caller falls through to the composed name it used before this leg. A page served an island
       from before this leg has no `alts` field at all and behaves the same way, which is what
       keeps a stale cached island from blanking an alt. */
    var ALTS = {};
    (G.alts || []).forEach(function (p) { ALTS[p[0]] = p[1]; });
    function authoredAlt(u) {
      return ALTS[fileOf(u)] || '';
    }

    /* RT-716, owner decision D8: THE SELECTED VARIANT'S OWN BOUND PHOTOGRAPH LEADS, ON ANY OPTION.
       `vmedia` is [variant id, url] for every variant with a bound photograph and `vsel` is the
       opening variant; hh-gallery.liquid emits both only where the variants bind different
       photographs, so `VM` is null everywhere else and the paths below run as before. The buy
       column hands over the variant id with every selection. A variant with no bind leaves the
       lead where it is. */
    var VM = null, curVar = null;
    if (G.vmedia && G.vmedia.length) {
      VM = {};
      G.vmedia.forEach(function (p) { VM[String(p[0])] = p[1]; });
      if (G.vsel != null) curVar = String(G.vsel);
    }
    function boundSrc() {
      return (VM && curVar != null && VM[curVar]) || '';
    }

    /* The filename, not the URL: the same photograph reaches this page through two prefixes, so
       comparing whole URLs would call one photograph two. Same key hh-gallery.liquid emits on. */
    function fileOf(u) {
      return u ? String(u).split('?')[0].split('/').pop() : '';
    }

    /* Does this photograph belong on the variant currently selected?

       `bindOnly` is the charter's first bound, expressed as an argument rather than as a comment:
       POSITION 1 IS SET BY THE BIND, NOT BY THE ALT. Asked with bindOnly, only a value that came
       from a variant bind can exclude a photograph, so no alt string can move a hero. Asked
       without it, an authored alt can withhold a SECONDARY, which is what puts the correct
       tooth-count photograph on the strip and keeps the other five off it. */
    function fits(u, bindOnly) {
      if (!AX) return true;
      var tags = AX.byFile[fileOf(u)];
      if (!tags) return true;                       // no claim about this photograph -> it shows
      for (var i = 0; i < tags.length; i++) {
        var t = tags[i];
        if (bindOnly && !t.b) continue;
        if (AX.sel[t.a] != null && AX.sel[t.a] !== t.v) return false;
      }
      return true;
    }

    /* RT-391: THE RAIL IS THE COLOURWAY'S SET, THEN THE PAGE'S, AND AN ENTRY NAMES ITSELF.
       RT-400: AND IT LEADS WITH THE SELECTED VARIANT'S OWN BOUND FRAME.

       The lead is chosen by the BIND alone and falls back to `imgs[0]`, so on every colourway in
       the store today it IS `imgs[0]`: no colourway carries more than one distinct bind, so no
       bind can exclude anything and the first entry always fits. Nothing about which photograph a
       page opens on has moved. It starts moving, correctly and with no further theme change, the
       day a store leg binds a frame to one variant rather than to a colourway.

       The order of everything after the lead is unchanged: the colourway's remaining photographs
       in their existing order, then the page-level frames in theirs. */
    /* RT-442: `authored` says whether this colourway's own photographs may use their authored
       alts. It is false on exactly one case, the D6 stand-in colourway, and hh-gallery.liquid
       states why. The page-level frames below are not affected either way: they have carried
       their own alt since RT-391 and a page frame is not a colourway. */
    function railItems(imgs, page, altbase, authored) {
      var src = imgs || [];
      var out = [];
      function nameOf(u) {
        return (authored && authoredAlt(u)) || altbase;
      }
      if (!src.length) {
        /* RT-611: a colorway with nothing placed. The frames keep `names` and `drawing` so
           selectColor can apply specification v3 section 3.3 rules 2 to 4 to them. */
        (page || []).forEach(function (p) {
          if (p && p.src && fits(p.src, false)) out.push({ src: p.src, alt: p.alt || altbase, names: p.names || [], drawing: !!p.drawing });
        });
        return out;
      }
      /* RT-716: the selected variant's bound photograph leads when it has one; if the set does not
         carry it, it goes in front. Otherwise the RT-400 rule below, unchanged. */
      var lead = -1, want = boundSrc();
      if (want) {
        for (var w = 0; w < src.length; w++) { if (fileOf(src[w]) === fileOf(want)) { lead = w; break; } }
        if (lead < 0) { src = [want].concat(src); lead = 0; }
      }
      if (lead < 0) {
        lead = 0;
        for (var i = 0; i < src.length; i++) { if (fits(src[i], true)) { lead = i; break; } }
      }
      out.push({ src: src[lead], alt: nameOf(src[lead]) });
      src.forEach(function (u, k) {
        if (k !== lead && fits(u, false)) out.push({ src: u, alt: nameOf(u) });
      });
      (page || []).forEach(function (p) {
        if (p && p.src && fits(p.src, false)) out.push({ src: p.src, alt: p.alt || altbase });
      });
      return out;
    }

    /* RT-611, specification v3 section 3.5, THE RAIL MODEL. When position 1 is a file it is rail
       item 1, pressed, and a rail that would hold only the hero stays hidden (`< 2`), as before.
       When position 1 is the well (`onWell`), no item is pressed and one frame is enough to show
       the rail: a frame reachable only from the rail must not be hidden by a count that assumed
       the hero was in it. */
    function buildRail(items, onWell) {
      rail.innerHTML = '';
      /* RT-742 (P35 F-17 c): the rail is a named group only while it holds buttons. An empty labeled
         group is dropped by Chromium but may be announced by another browser. */
      if (!items || items.length < (onWell ? 1 : 2)) {                   // single image -> no rail (real empty-state)
        rail.removeAttribute('role');
        rail.removeAttribute('aria-label');
        return;
      }
      rail.setAttribute('role', 'group');
      rail.setAttribute('aria-label', 'More product images');
      items.forEach(function (it, k) {
        var src = it.src, altbase = it.alt;
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'hh-railbtn';
        /* RT-384, executing P34-RT351-5. THE NUMBER HAD NOTHING TO SAY WHAT IT COUNTED.
           This built `<altbase> <k+1>`, so a screen reader announced "12-Speed MTB Cassette in
           Black 1" and "X-Hanger UDH in Red 2". A bare trailing integer after a product name
           reads as part of the name, and nothing in the name said how many buttons there were,
           so the one control that tells a shopper where they are in a set told them neither.

           P34 authored the replacement and it is used unchanged: the ordinal is named as an
           image and the set is sized, so the name carries its own denominator.

           IT DEGRADES THROUGH EVERY BRANCH ABOVE IT, which is why it goes here and not in
           selectColor. `altbase` is already the one expression the three RT-222 and D6 cases
           agree on, and the `|| 'View'` fallback is kept for the no-`t2` path, so a page with no
           product line name still gets "View, image 1 of 4" rather than a leading comma.
           `items.length` is the array this loop is walking, so the count cannot disagree with the
           number of buttons the loop makes.

           RT-383 is binding variant images to live products while this ships, so which pictures
           a colourway holds is moving. Nothing here depends on that: the denominator is read off
           the array at build time, so a rail that gains an image renames itself. RT-391 puts
           page-level frames into the same array, so they are counted in the same denominator and
           `altbase` is now this entry's own name rather than the whole set's. */
        b.setAttribute('aria-label', (altbase || 'View') + ', image ' + (k + 1) + ' of ' + items.length);
        b.setAttribute('aria-pressed', (k === 0 && !onWell) ? 'true' : 'false');
        var im = document.createElement('img');
        im.src = src; im.alt = ''; im.loading = 'lazy';
        b.appendChild(im);
        b.addEventListener('click', function () {
          rail.querySelectorAll('button').forEach(function (x) { x.setAttribute('aria-pressed', 'false'); });
          b.setAttribute('aria-pressed', 'true');
          setHero(src, altbase);
        });
        rail.appendChild(b);
      });
    }

    function selectColor(name) {
      if (!G.hasColor) return;
      if (name != null) curColor = name;   // RT-400: so a size-only change knows which colour to re-render
      var c = (G.colors || []).find(function (x) { return x.name === name; });
      /* RT-611: an empty `imgs` is now a normal state (specification v3 section 3.4), not a
         defect, so only a name the island does not carry at all returns here. */
      if (!c) return;
      /* RT-222: DO NOT NAME A COLOURWAY THE PICTURE DOES NOT SHOW.
         `placed` is false when hh-gallery.liquid could resolve no photograph for this colourway
         and handed down the product's featured image instead. Saying "in Violet" over it is a
         false statement about the document, and it is the only statement a screen-reader user
         gets. On the false case the alt falls back to the product line name, which is exactly
         what a product with no Color option already renders. Older markup without the field
         keeps the previous behaviour (`!== false`), so a stale cached island cannot blank alts.

         RT-345, owner ruling D6: A STAND-IN SAYS WHAT IT ACTUALLY SHOWS.
         `c.shown` is present only when the photograph on this colourway is a real photograph of
         the SAME product in ANOTHER colour. RT-222 stopped the page naming a colour when nothing
         placed a picture; D6 covers the case where something did place one and it is honestly a
         different colour, so the name becomes "<t2>, shown in <that colour>" rather than the
         untrue "<t2> in <the colourway the shopper picked>". hh-gallery.liquid emits the field
         only alongside placed:true, so the two rules cannot both fire; the `placed` test is kept
         first anyway so this reads in the order the rules were decided.

         ONE EXPRESSION, THREE SURFACES. `alt` goes to the hero (setHero) and to the rail button
         labels (buildRail), and the lightbox copies heroImg.alt when it opens. Nothing else has
         to learn about D6. */
      var alt;
      if (!G.t2) alt = name;
      else if (c.placed === false) alt = G.t2;
      else if (c.shown) alt = G.t2 + ', shown in ' + c.shown;
      else alt = G.t2 + ' in ' + name;
      /* RT-391: the hero is still `imgs[0]`, so nothing about which photograph a colourway opens
         on has moved. The rail is the colourway's own set followed by the page-level frames.
         RT-611: that sentence holds for every colourway with a non-empty `imgs`, and that path
         below is unchanged. A colourway with an empty `imgs` takes the branch after it. */
      /* RT-400: the hero is the FIRST RAIL ENTRY rather than `imgs[0]` written out a second time,
         so the two cannot disagree about which photograph is position 1. On every colourway in the
         store today they are the same URL, for the reason given above railItems. */
      /* RT-442: the hero takes the FIRST RAIL ENTRY'S NAME as well as its src, so the photograph
         and the sentence announced over it cannot come from two different places. `alt` stays the
         fallback for the case where there is no entry to take a name from. */
      if (c.imgs && c.imgs.length) {
        var items = railItems(c.imgs, c.page, alt, !c.shown);
        setHero(items.length ? items[0].src : c.imgs[0], items.length ? items[0].alt : alt);
        buildRail(items);
        return;
      }

      /* RT-611, Gallery Chip Specification v3 section 3.3, owner decisions D103 and D106. NOTHING
         IS PLACED FOR THIS COLOURWAY, so position 1 is the first of:
           2. on a page that sells ONE colour, the first page-level frame ("keep the picture");
           3. else the first frame whose alt NAMES this colourway (hh-gallery.liquid's naming pass);
           4. else the first frame whose alt says it is a drawing;
           5. else the "Photo Coming Soon" well, with zoom off.
         Each frame keeps its own authored alt; the D6 composed name is never written over it.
         hh-gallery.liquid applies the same rules to the server-rendered hero, so the page does not
         change picture when this script runs. */
      var frames = railItems(null, c.page, alt, true);
      var lead = -1, i;
      if ((G.colors || []).length === 1) {
        if (frames.length) lead = 0;
      } else {
        for (i = 0; i < frames.length; i++) { if (frames[i].names.indexOf(name) !== -1) { lead = i; break; } }
        if (lead < 0) {
          for (i = 0; i < frames.length; i++) { if (frames[i].drawing) { lead = i; break; } }
        }
      }
      if (lead >= 0) {
        var ordered = [frames[lead]].concat(frames.filter(function (f, k) { return k !== lead; }));
        setHero(ordered[0].src, ordered[0].alt);
        buildRail(ordered);
      } else if (showWell()) {
        buildRail(frames, true);
      }
    }

    /* RT-400: the buy column tells the gallery the WHOLE selection, not just the colour.

       It is one entry point rather than two because a repair on a sparse grid can move several
       axes at once, and a gallery that learned about the colour from one call and the size from
       another would render an item the form does not hold. `opts` is keyed by option NAME, which
       is what `axisOf` is keyed on and what the shopper's controls are labelled with; positions
       are an internal detail of the buy column and stay there.

       Values for axes the island carries no photograph for are ignored, so a page with nothing to
       say on its second axis does exactly what it did before: re-render the colour. */
    var curColor = null;
    /* RT-716: a page with no Colour option. Only a bound variant moves the hero; the rail is
       rebuilt so its first, pressed entry is the hero, as on every other path. */
    function showVariant() {
      if (G.hasColor || !boundSrc()) return;
      var items = railItems(G.fallback, null, G.t2, true);
      if (!items.length) return;
      setHero(items[0].src, items[0].alt);
      buildRail(items);
    }
    function setSelection(opts, vid) {
      if (!opts) return;
      if (vid != null) curVar = String(vid);
      if (!G.hasColor) { showVariant(); return; }
      var cname = null;
      Object.keys(opts).forEach(function (k) {
        if (/colo(u)?r/i.test(k)) cname = opts[k];
        else if (AX && AX.sel.hasOwnProperty(k)) AX.sel[k] = opts[k];
      });
      if (cname != null) curColor = cname;
      selectColor(curColor);
    }

    /* ENLARGED-IMAGE DIALOG (RT-099 accessibility finding).
       The old lightbox was a div toggled with aria-hidden: no dialog role, no focus trap, and
       the page behind stayed in the tab order, which made it mouse-only in practice. It is now
       a native <dialog> driven by showModal(), so the dialog role, aria-modal, the focus trap,
       Escape and the inert background all come from the platform.
       FALLBACK: browsers without showModal get the non-modal <dialog open> path, which still
       shows the image and still closes on Escape and on click. */
    if (lb && lbImg) {
      var canModal = typeof lb.showModal === 'function';
      if (!wellShown()) hero.style.cursor = 'zoom-in';

      function openLb() {
        /* RT-611: the lightbox never opens on the well (specification v3 section 4 item 9). */
        if (wellShown()) return;
        lbImg.src = heroImg.src;
        lbImg.alt = heroImg.alt || '';
        if (canModal) {
          lb.showModal();
        } else {
          lb.setAttribute('open', '');
          lb.setAttribute('tabindex', '-1');
          lb.focus();
        }
      }

      function closeLb() {
        if (canModal) {
          if (lb.open) lb.close();
        } else if (lb.hasAttribute('open')) {
          lb.removeAttribute('open');
          focusZoom();
        }
      }

      /* RT-742: the zoom <button> sits inside the figure, so its Enter, Space and click reach this
         one listener by bubbling. The mouse path is the figure itself, as before (the badge has
         pointer-events:none). */
      function focusZoom() {
        if (zoomBadge && zoomBadge.style.display !== 'none') zoomBadge.focus();
      }
      hero.addEventListener('click', openLb);

      // click the backdrop, the surrounding area, or the close button
      lb.addEventListener('click', function (e) {
        if (e.target === lb || e.target.closest('[data-hh-lbclose]') || e.target === lbImg) closeLb();
      });

      // native <dialog> returns focus itself, but be explicit so the fallback path matches
      lb.addEventListener('close', function () { focusZoom(); });

      if (!canModal) {
        document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeLb(); });
      }
    }

    // initial state: the buy column's selected (or first) colour
    if (G.hasColor) {
      selectColor(G.selected || (G.colors[0] && G.colors[0].name));
    } else {
      /* RT-391: the no-Colour-option path is unchanged. `fallback` already carries every media
         item on the product, so page-level frames were never missing here; this is the same list
         through the new entry shape. */
      /* RT-442: `true` because a product with no Colour option has no colourway to name and
         therefore no stand-in marker either: every one of these frames says what it shows, and
         until this leg every one of them announced the product line name instead. */
      buildRail(railItems(G.fallback, null, G.t2, true));
    }
    return { selectColor: selectColor, setSelection: setSelection };
  }

  /* ----------------------------- quantity stepper -----------------------------
     RT-226 / RT222-P77-3. The markup and the reasoning are in hh-buy-column.liquid; this is the
     behaviour half of the same ruling.

     IT IS INITIALISED SEPARATELY FROM THE SELECTORS, ON PURPOSE. A single-variant product renders
     no selector control at all (`option.values.size > 1`), and 32 of the 63 W2 pages are exactly
     that. Hanging the stepper off initBuyColumn's variant island would have left it dead on the
     pages where it is the ONLY row in the buy column. It is its own island, and initBuyColumn is
     handed a reset() rather than owning the control.

     MINIMUM 1, NO MAXIMUM. The floor is enforced in three places that cannot disagree, because
     each covers an input the others do not: the `-` button (disabled at 1), typed text (clamped on
     change and on blur), and the field's own `min="1"`. There is deliberately no ceiling: the
     owner's standing ruling is that live Shopify inventory is the only stock truth and the cutover
     writes no quantities, so a rendered ceiling would assert a number we do not hold. The cart
     enforces.

     WHY THE CLAMP IS NOT ON `input`. Clearing the field to retype it would rewrite it to 1 under
     the shopper's cursor mid-keystroke. `input` therefore only refreshes the button's disabled
     state; the VALUE is settled on `change` and `blur`, which is when the shopper has finished. */
  function initQuantity() {
    var group = document.querySelector('[data-hh-qty]');
    if (!group) return null;
    var field = group.querySelector('[data-hh-qty-val]');
    if (!field) return null;
    var dec = group.querySelector('[data-hh-qty-step="-1"]');

    /* The value the field is CURRENTLY holding, as a legal quantity. Anything unreadable, empty,
       zero, negative or fractional resolves to the floor; parseInt truncates "2.7" to 2. */
    function read() {
      var n = parseInt(field.value, 10);
      return (isFinite(n) && n >= 1) ? n : 1;
    }

    function write(n) {
      field.value = String(n);
      if (dec) dec.disabled = n <= 1;
    }

    group.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('[data-hh-qty-step]') : null;
      if (!b || b.disabled) return;
      /* An empty field counts as 0 here rather than as the floor, so pressing "+" on a cleared
         field lands on 1 and not on 2. */
      var cur = parseInt(field.value, 10);
      var base = (isFinite(cur) && cur >= 1) ? cur : 0;
      write(Math.max(1, base + parseInt(b.getAttribute('data-hh-qty-step'), 10)));
    });

    field.addEventListener('input', function () { if (dec) dec.disabled = read() <= 1; });
    field.addEventListener('change', function () { write(read()); });
    field.addEventListener('blur', function () { write(read()); });

    /* Settle the control against whatever value the field actually holds at load. The server
       renders 1 with `-` disabled, but a browser restoring a form on back-navigation can hand us
       a 5 with the button still disabled, and the two must not disagree. */
    write(read());

    return { reset: function () { write(1); } };
  }

  /* ------------------------- buy-column selectors ------------------------- */
  function initBuyColumn(gallery, qty) {
    var root = document.querySelector('.hh-buy');
    var dataEl = document.querySelector('[data-hh-variants]');
    if (!root || !dataEl) return;
    var DATA;
    try { DATA = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!DATA || !DATA.variants) return;

    // which option position is Colour (drives the gallery)? match by name, never by value.
    var colorPos = null;
    (DATA.options || []).forEach(function (o) {
      if (/colo(u)?r/i.test(o.name)) colorPos = String(o.position);
    });

    var sel = {}; // position -> chosen value
    root.querySelectorAll('[data-hh-opt]').forEach(function (b) {
      if (b.getAttribute('aria-checked') === 'true') sel[b.getAttribute('data-hh-opt')] = b.getAttribute('data-hh-value');
    });
    /* RT-216: seed the <select> axes too. Only the BUTTON controls carry aria-checked, so an axis
       rendered as a native menu (hh-select-text's non-compact form: > 4 values, or any value over
       14 characters) was left out of `sel` entirely and match() treated it as a wildcard. On a full
       grid that was harmless because the wildcard resolved to the same variant the server had
       already selected. On a SPARSE grid it is not harmless, so the seed is made explicit. */
    root.querySelectorAll('[data-hh-optselect]').forEach(function (m) {
      sel[m.getAttribute('data-hh-optselect')] = m.value;
    });

    /* ------------------------------------------------------------------------------------
       RT-742 (P35 RT-671 F-02, F-01). ANNOUNCE AND RESTORE.

       F-02: arrow keys select on focus inside a radio group, so a keyboard customer who only
       PASSES a value that cannot combine with their size had the size repaired away (rule 1
       below) and it stayed changed, with nothing said. P77's RT222-P77-4 keeps marked values
       SELECTABLE, so the cure is not a disabled control. It is two rules on top of RT-216:

       A. RESTORE. `pref` holds the value the customer last chose on EACH axis, which is not always
          the value shown: a repair moves `sel` and leaves `pref` alone. Every later choice first
          tries the customer's own choices on the other axes, so the size they picked comes back
          as soon as they move to a value that can carry it. A repair is scored first on `pref`
          and then on `sel`, so it keeps as much of what the customer asked for as it can.

       B. ANNOUNCE. Every change to ANOTHER selector is said in words ("Size changed to 30.0 mm"),
          and so is the size coming back. The control the customer is on is not repeated: it
          announces itself.

       F-01: one polite status region carries what changed for the item in the form: the other
       selectors, then the price (only when it changes), then the stock state (only when it
       changes, with the lead-time note when one appears). Focus never moves. The price line is
       no longer a live region, so an unchanged price is no longer the one thing a customer hears.
       ------------------------------------------------------------------------------------ */
    var pref = {};
    Object.keys(sel).forEach(function (k) { pref[k] = sel[k]; });
    var liveEl = root.querySelector('[data-hh-live]');
    var liveTimer = null;
    function announce(parts) {
      if (!liveEl || !parts.length) return;
      var msg = parts.join('. ') + '.';
      /* cleared first and written on the next tick, so the same words said twice in a row (a size
         repaired away and later repaired away again) are still announced the second time */
      liveEl.textContent = '';
      if (liveTimer) clearTimeout(liveTimer);
      liveTimer = setTimeout(function () {
        liveEl.textContent = msg;
        /* plan lens finding 4: the words do not linger beside the stock line after they are said */
        liveTimer = setTimeout(function () { liveEl.textContent = ''; liveTimer = null; }, 6000);
      }, 100);
    }

    /* ------------------------------------------------------------------------------------
       RT-216 — SPARSE VARIANT GRIDS. P25's RT-210 Oval ruling put a second value on the
       `Chainring shape` axis of two records WITHOUT filling the cross product behind it:
       `garbaruk-dm-road-gravel-chainring-garbaruk` carries 50 of 96 combinations (Oval exists
       in Black 42T and Black 44T alone) and `garbaruk-dm-mtb-chainring-garbaruk-crankset`
       carries 42 of 80 (Oval in Black 32T and Silver 34T alone). They are the ONLY two sparse
       grids in the ACTIVE catalogue; the other 102 products fill their grid exactly, measured in
       `apply/probe_rt216_shape.py`. Everything below is therefore inert on 102 of 104 pages.

       WHAT WENT WRONG BEFORE, and it was worse than cosmetic. `apply()` opened with
       `var v = match(); if (!v) return;`. Choosing Oval while Blue and 38T were selected resolved
       to no variant, so the function returned having changed nothing, while `choose()` had ALREADY
       written "Oval" into the axis label and moved the checked state. The page then read Oval and
       the hidden /cart/add `id`, the price, the weight and the stock line all still belonged to the
       ROUND variant. Add to cart would have put a round chainring in the basket of somebody who
       had asked for an oval one and been shown no contradiction.

       THE FIX IS TWO RULES.

       1. REPAIR, NEVER STALL. The value the shopper just touched is authoritative. If the
          combination it produces does not exist, the OTHER axes move to the nearest combination
          that does, and the controls visibly follow. The variant shown is always the variant in
          the form.

       2. SAY WHICH VALUES ARE IMPOSSIBLE, before they are clicked. After every selection each
          value is tested against the current choices on the other axes and marked when no variant
          exists. Marked values stay CLICKABLE, because rule 1 makes clicking them meaningful: it
          moves the other axes rather than failing. That is what stops the grid reading as broken.
          Pick Oval and six of eight colours and four of five sizes go visibly quiet at once, which
          states the narrow Oval range instead of leaving it to be discovered by trial.

       This is about variant EXISTENCE, not stock. A sold-out variant is untouched here and keeps
       reporting through the stock line, which matters because the owner's standing rule is that
       Garbaruk lists every colour and out-of-stock ones simply read Out of stock.
       ------------------------------------------------------------------------------------ */

    /* Which axes bear on FIT rather than on taste. Used only to break a tie when repairing: if a
       shopper's exact combination does not exist, changing their colour is a smaller harm than
       changing the size they need to fit a crank. Names are matched, never positions. */
    function fitWeight(name) {
      return /size|cog|range|speed|interface|freehub|spline|shape|width|length|bcd|offset/i.test(name) ? 4 : 1;
    }

    function findExact(want) {
      return DATA.variants.find(function (v) {
        return DATA.options.every(function (o) {
          var chosen = want[o.position];
          return chosen == null || v.options[o.position - 1] === chosen;
        });
      });
    }

    /* The nearest existing variant that HONOURS the value just chosen. Scored on how much of the
       shopper's remaining selection it keeps, weighted by fitWeight; ties go to an available
       variant, then to the product's own variant order so the result is deterministic. */
    function repair(pos, val) {
      var best = null, bestScore = -1;
      DATA.variants.forEach(function (v) {
        if (v.options[pos - 1] !== val) return;
        var score = 0;
        DATA.options.forEach(function (o) {
          if (String(o.position) === String(pos)) return;
          var got = v.options[o.position - 1];
          /* RT-742 rule A (plan lens finding 1): the value shown now counts double, so a repair moves as
             little as it can (RT-216 rule 1); the customer's earlier choice on this axis breaks a tie. */
          if (sel[o.position] != null && got === sel[o.position]) score += 2 * fitWeight(o.name);
          if (pref[o.position] != null && got === pref[o.position]) score += fitWeight(o.name);
        });
        // strictly better only, so an equal candidate later in the list never displaces an
        // earlier one and the repair is deterministic on the product's own variant order
        var better = score > bestScore || (score === bestScore && v.available && !best.available);
        if (best === null || better) { best = v; bestScore = score; }
      });
      return best;
    }

    /* Write `sel` back into every control, so a repair is visible on the axes it moved and not
       only on the one that was clicked. */
    function syncControls() {
      root.querySelectorAll('[data-hh-opt]').forEach(function (b) {
        var pos = b.getAttribute('data-hh-opt');
        var on = b.getAttribute('data-hh-value') === sel[pos];
        b.setAttribute('aria-checked', String(on));
        var group = b.parentElement;
        if (group && group.getAttribute('role') === 'radiogroup') b.setAttribute('tabindex', on ? '0' : '-1');
        else b.setAttribute('aria-pressed', String(on));
      });
      root.querySelectorAll('[data-hh-optselect]').forEach(function (m) {
        var pos = m.getAttribute('data-hh-optselect');
        if (sel[pos] != null && m.value !== sel[pos]) m.value = sel[pos];
      });
      DATA.options.forEach(function (o) {
        var nameEl = root.querySelector('[data-hh-sel-name="' + o.position + '"]');
        if (nameEl && sel[o.position] != null) nameEl.textContent = sel[o.position];
      });
    }

    /* Does a variant exist with `val` on `pos` and the shopper's current values everywhere else? */
    function combinable(pos, val) {
      var want = {};
      DATA.options.forEach(function (o) { if (sel[o.position] != null) want[o.position] = sel[o.position]; });
      want[pos] = val;
      return !!findExact(want);
    }

    /* Dawn's own shipped string, `products.product.unavailable`, reused verbatim rather than
       authored here: this is interface state, and there was no reason to write a new word for it. */
    var UNAVAIL = 'Unavailable';

    function refreshAvailability() {
      root.querySelectorAll('[data-hh-opt]').forEach(function (b) {
        var pos = b.getAttribute('data-hh-opt');
        var val = b.getAttribute('data-hh-value');
        if (b.dataset.hhLabel == null) {
          // the server-rendered accessible name, kept so the suffix can come off again
          b.dataset.hhLabel = b.getAttribute('aria-label') || '';
        }
        var ok = combinable(pos, val);
        var base = b.dataset.hhLabel || (b.textContent || '').trim();
        if (ok) {
          b.removeAttribute('data-hh-unavail');
          if (b.dataset.hhLabel) b.setAttribute('aria-label', b.dataset.hhLabel);
          else b.removeAttribute('aria-label');
        } else {
          b.setAttribute('data-hh-unavail', 'true');
          b.setAttribute('aria-label', base + ', ' + UNAVAIL);
        }
      });
      root.querySelectorAll('[data-hh-optselect]').forEach(function (m) {
        var pos = m.getAttribute('data-hh-optselect');
        Array.prototype.forEach.call(m.options, function (op) {
          if (op.dataset.hhLabel == null) op.dataset.hhLabel = op.textContent;
          // a menu has no visual channel, so the state has to ride in the option's own text
          op.textContent = combinable(pos, op.value) ? op.dataset.hhLabel
                                                     : op.dataset.hhLabel + ' - ' + UNAVAIL;
        });
      });
    }

    function apply(v) {
      if (!v) v = findExact(sel);
      if (!v) return null;
      var said = [];   // RT-742 F-01: what changed, in words, for the status region
      var idEl = root.querySelector('[data-hh-id]');
      /* RT-226: read the outgoing variant BEFORE overwriting it. A quantity chosen for one
         colourway must not silently follow the shopper to another, so the stepper resets to 1 when
         the item in the form actually changes. Keyed on the variant id rather than on "a control
         was touched", because a repair can move axes without changing the variant and re-picking
         the value already selected changes nothing and should not discard a typed quantity. */
      var variant_changed = !!idEl && String(idEl.value) !== String(v.id);
      if (idEl) idEl.value = v.id;
      if (variant_changed && qty) qty.reset();
      var priceEl = root.querySelector('[data-hh-price]');
      if (priceEl && v.price && priceEl.textContent !== v.price) { priceEl.textContent = v.price; said.push(v.price); }
      /* RT-1208 (D393-8): the struck full price sits before the price only while the variant carries
         `compare` (hh-variant-data sets it only when the compare-at is above the price). It is made or
         removed here with the one space after it, so the block matches what hh-buy-column renders. */
      if (priceEl) {
        var wasEl = root.querySelector('[data-hh-compare]');
        if (v.compare) {
          if (!wasEl) {
            wasEl = document.createElement('s');
            wasEl.className = 'hh-price__was';
            wasEl.setAttribute('data-hh-compare', '');
            priceEl.parentNode.insertBefore(wasEl, priceEl);
            priceEl.parentNode.insertBefore(document.createTextNode(' '), priceEl);
          }
          if (wasEl.textContent !== v.compare) wasEl.textContent = v.compare;
        } else if (wasEl) {
          var gap = wasEl.nextSibling;
          if (gap && gap.nodeType === 3) gap.parentNode.removeChild(gap);
          wasEl.parentNode.removeChild(wasEl);
        }
      }
      var wEl = root.querySelector('[data-hh-weight]'); if (wEl && v.grams) wEl.textContent = v.grams + ' g';
      var state = v.state || (v.available ? 'in-stock' : 'sold-out');
      var stockEl = root.querySelector('[data-hh-stock]');
      var stockWas = stockEl ? stockEl.textContent : null;
      if (stockEl) {
        stockEl.className = 'hh-stock hh-stock--' + state;
        stockEl.textContent = state === 'in-stock' ? 'In stock' : (state === 'lead-time' ? 'Made to order' : 'Sold out');
        if (stockEl.textContent !== stockWas) said.push(stockEl.textContent);
      }
      var leadEl = root.querySelector('[data-hh-lead]');
      if (leadEl) {
        var leadWas = leadEl.hidden ? '' : leadEl.textContent;
        if (state === 'lead-time') { leadEl.hidden = false; if (v.lead) leadEl.textContent = v.lead; }
        else { leadEl.hidden = true; }
        if (!leadEl.hidden && leadEl.textContent && leadEl.textContent !== leadWas) {
          var note = leadEl.textContent.replace(/\.\s*$/, '');
          /* plan lens finding 5: a note that opens with the stock words replaces them rather than repeating them */
          if (said.length && note.indexOf(said[said.length - 1]) === 0) said[said.length - 1] = note;
          else said.push(note);
        }
      }
      var cart = root.querySelector('[data-hh-cart]');
      if (cart) { cart.disabled = !v.available; cart.textContent = v.available ? 'Add to cart' : 'Sold out'; }
      return said;
    }

    /* choose(pos, val): the one place a selection is committed, whichever control made it.
       Both the button controls and the <select> route through here, so the variant id, price,
       weight, stock line, label echo, the marked-unavailable state and the gallery stay in step
       no matter the axis's form.

       RT-216 changed its shape. It no longer writes `sel[pos]` and hopes a variant turns up; it
       RESOLVES A VARIANT FIRST and then takes the whole of `sel` from that variant's own option
       values. Every axis therefore describes the item in the form, including the axes a repair
       moved, and the "chose Oval, still holding a Round in the cart" state is unreachable rather
       than merely unlikely. `group` is no longer needed to paint the checked state, since
       syncControls() paints all of them; it is kept out of the signature entirely. */
    function choose(pos, val) {
      /* RT-742 rule A: the customer's own choices first (`pref`), not the values a repair left. */
      var want = {};
      DATA.options.forEach(function (o) {
        var p = pref[o.position] != null ? pref[o.position] : sel[o.position];
        if (p != null) want[o.position] = p;
      });
      want[pos] = val;

      /* RT-742 plan lens finding 1: when the customer's own choices cannot combine, the combination on
         screen is tried next, and only then a repair. So a repair never replaces a value the customer
         can still see and keep. */
      var cur = {};
      DATA.options.forEach(function (o) { if (sel[o.position] != null) cur[o.position] = sel[o.position]; });
      cur[pos] = val;
      var v = findExact(want) || findExact(cur) || repair(pos, val);
      if (!v) return;                       // a value with no variant at all: leave the page alone

      pref[pos] = val;
      var before = {};
      DATA.options.forEach(function (o) { before[o.position] = sel[o.position]; });
      DATA.options.forEach(function (o) { sel[o.position] = v.options[o.position - 1]; });

      syncControls();
      /* RT-742 rule B: every OTHER selector that moved is said in words, in option order. */
      var said = [];
      DATA.options.forEach(function (o) {
        if (String(o.position) === String(pos)) return;
        if (before[o.position] != null && before[o.position] !== sel[o.position]) {
          said.push(o.name + ' changed to ' + sel[o.position]);
        }
      });
      said = said.concat(apply(v) || []);
      refreshAvailability();
      announce(said);
      /* RT-400: hand the gallery the WHOLE selection, every time, not the colour when the colour
         moved. It was gated on `sel[colorPos] !== before`, which is exactly the gate the owner's
         D8 is about: changing tooth count or chainring size left the gallery untouched because the
         colour had not moved. The gallery decides for itself what changed and re-renders the
         colourway it is already on, so a page with nothing to say about a second axis repaints the
         same rail it was showing. A sparse-grid repair that moves several axes at once arrives as
         one call, so the pictures and the form cannot describe different items. */
      pushSelection(v.id);
    }

    /* The current selection keyed by option NAME, which is what the gallery's data is keyed on. */
    function galleryOpts() {
      var o = {};
      DATA.options.forEach(function (x) { if (sel[x.position] != null) o[x.name] = sel[x.position]; });
      return o;
    }

    function pushSelection(vid) {
      if (gallery && gallery.setSelection) gallery.setSelection(galleryOpts(), vid);
    }

    root.querySelectorAll('[data-hh-opt]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        choose(btn.getAttribute('data-hh-opt'), btn.getAttribute('data-hh-value'));
      });
    });

    /* RT-120: arrow-key movement inside a radiogroup (ARIA authoring practice, WCAG 2.1.1).
       The colour swatches keep role="group" and stay individually tabbable, since a grid of
       swatches is not a radiogroup. */
    root.querySelectorAll('[data-hh-radiogroup]').forEach(function (group) {
      var radios = Array.prototype.slice.call(group.querySelectorAll('[role="radio"]'));
      group.addEventListener('keydown', function (e) {
        var i = radios.indexOf(document.activeElement);
        if (i < 0) return;
        var next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = radios[(i + 1) % radios.length];
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = radios[(i - 1 + radios.length) % radios.length];
        else if (e.key === 'Home') next = radios[0];
        else if (e.key === 'End') next = radios[radios.length - 1];
        if (!next) return;
        e.preventDefault();
        next.focus();
        choose(next.getAttribute('data-hh-opt'), next.getAttribute('data-hh-value'));
      });
    });

    /* RT-120: the native <select> form of a textual axis. */
    root.querySelectorAll('[data-hh-optselect]').forEach(function (menu) {
      menu.addEventListener('change', function () {
        choose(menu.getAttribute('data-hh-optselect'), menu.value);
      });
    });

    /* RT-216: paint the marked-unavailable state once at load, from the server-selected variant.
       On the 102 full-grid products nothing is marked and nothing in the DOM changes. */
    refreshAvailability();
  }

  function init() {
    var gallery = initGallery();
    var qty = initQuantity();
    initBuyColumn(gallery, qty);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
